"use client";

import React from "react";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { ChipFilterGroup } from "@/components/ChipFilterGroup/ChipFilterGroup";
import { DashboardFiltersFormData } from "@/modules/stochastic/features/SalesAndCustomerDashboard/hooks/dashboardFiltersFormSchema";
import { useStochasticDashboardFilters } from "@/modules/stochastic/features/SalesAndCustomerDashboard/hooks/useStochasticDashboardFilters";
import { DashboardTabs } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/DashboardTabs/DashboardTabs";
import { UseFormReturn } from "react-hook-form";

type TabType = "sales" | "customers" | "review";

interface StochasticDashboardFiltersProps {
  chartFiltersForm: UseFormReturn<DashboardFiltersFormData>;
  onApplyFilters: () => void;
  areFiltersDirty: boolean;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

export const StochasticDashboardFilters: React.FC<
  StochasticDashboardFiltersProps
> = ({
  chartFiltersForm,
  onApplyFilters,
  areFiltersDirty,
  activeTab,
  setActiveTab,
}) => {
  const {
    filterConfigs,
    filterValues,
    handleFilterChange,
    handleFilterClearAll,
  } = useStochasticDashboardFilters(chartFiltersForm);

  return (
    <div className="mb-4 space-y-4">
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <FunnelIcon className="h-4 w-4" />
            <span className="text-sm font-medium">Filters</span>
          </div>

          <ChipFilterGroup
            filters={filterConfigs}
            onChange={handleFilterChange}
            onClearAll={handleFilterClearAll}
            showActiveFilters={false}
            showChips={true}
            values={filterValues}
          />

          <div className="flex items-center gap-3 ml-auto">
            {areFiltersDirty && (
              <span className="text-yellow-600 text-sm">Filters changed</span>
            )}
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={onApplyFilters}
              type="button"
            >
              Apply Filters
            </Button>
          </div>
        </div>

        <ChipFilterGroup
          filters={filterConfigs}
          onChange={handleFilterChange}
          onClearAll={handleFilterClearAll}
          showActiveFilters={true}
          showChips={false}
          values={filterValues}
        />
      </div>

      <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};
